/**
 * Touch controls for phones and tablets.
 *
 * This is a thin skin over Input's virtual layer, not a second input system:
 * the joystick writes an analog move vector, buttons press the same key codes
 * the keyboard would, and dragging the screen injects look deltas where mouse
 * movement lands. Game code cannot tell a thumb from a keyboard, which is the
 * entire point — every binding, stance rule and minigame works unmodified.
 *
 * Layout, in landscape:
 *
 *   [torch radio mic pause]                      ← top right, one-shot taps
 *                             [interact]         ← appears with the prompt
 *   (joystick)                [sprint crouch]
 *                                  [ context ]   ← appears when it means
 *                                                  something: LET GO, BREATH,
 *                                                  SWITCH
 *
 * There is no use button: hotbar slots are the use buttons — tap to select
 * and use, hold to drop. Items are occasional; a permanent thumb-sized
 * circle for them was screen spent on the wrong frequency.
 *
 * Everything that is not a control is a look surface. The right thumb drags
 * anywhere to turn; multi-touch is tracked by pointer id so steering and
 * looking never steal from each other.
 */

import type { Input } from './Input.js';

/** Radians of look per CSS pixel of drag. */
const LOOK_SENSITIVITY = 0.0052;
/** Joystick throw in CSS pixels from centre to full deflection. */
const STICK_RADIUS = 52;
/** Deflection below this is noise from a resting thumb. */
const STICK_DEADZONE = 0.14;

export interface TouchSyncState {
  torchOn: boolean;
  radioOn: boolean;
  micStatus: 'off' | 'starting' | 'live' | 'denied';
  micMuted: boolean;
  hiding: boolean;
  climbing: boolean;
  /** The interact prompt text, or null when nothing is near. */
  prompt: string | null;
  /** Prompt is informational only — a full hiding place. Shown, not offered. */
  promptDisabled: boolean;
  /** True while down and watching a teammate; movement controls go away. */
  spectating: boolean;
  /** True while the transmit minigame owns the screen. */
  transmitting: boolean;
  /** True once a map is carried; shows the MAP toggle. */
  hasMap: boolean;
  /** Whether the map panel is currently open. */
  mapOpen: boolean;
}

export class TouchControls {
  private readonly root: HTMLDivElement;
  private readonly stick: HTMLDivElement;
  private readonly nub: HTMLDivElement;
  private readonly contextBtn: HTMLDivElement;
  private readonly sprintBtn: HTMLDivElement;
  private readonly crouchBtn: HTMLDivElement;
  private readonly interactBtn: HTMLDivElement;
  private readonly torchBtn: HTMLDivElement;
  private readonly radioBtn: HTMLDivElement;
  private readonly micBtn: HTMLDivElement;
  private readonly mapBtn: HTMLDivElement;
  private readonly pauseBtn: HTMLDivElement;
  private readonly tuneLeft: HTMLDivElement;
  private readonly tuneRight: HTMLDivElement;

  private stickPointer = -1;
  private stickCentre = { x: 0, y: 0 };
  private lookPointer = -1;
  private lookLast = { x: 0, y: 0 };

  /** Toggle states. Held keys are momentary; these two are taps. */
  private sprintOn = false;
  private crouchOn = false;
  /** Last synced spectating state, to catch the transition into it. */
  private spectating = false;

  constructor(
    parent: HTMLElement,
    private readonly input: Input,
  ) {
    this.root = div('touch-controls');

    // --- Look surface: the whole overlay background ---------------------------
    // Move and release are tracked on window, not the overlay: a thumb that
    // slides off the screen edge or loses its pointerup to a system gesture
    // must still release, or the control it held wedges forever.
    this.root.addEventListener('pointerdown', this.onLookDown);
    window.addEventListener('pointermove', this.onPointerMove);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerUp);
    this.root.addEventListener('contextmenu', (e) => e.preventDefault());

    // --- Joystick -------------------------------------------------------------
    this.stick = div('tc-stick');
    this.nub = div('tc-nub');
    this.stick.appendChild(this.nub);
    this.stick.addEventListener('pointerdown', this.onStickDown);
    this.root.appendChild(this.stick);

    // --- Right-thumb cluster --------------------------------------------------
    /**
     * The contextual Space button.
     *
     * Space no longer jumps — there is no jump — so this button exists only
     * for the three things it still does, and is hidden entirely when none of
     * them apply. That gives the thumb cluster its largest slot back for the
     * whole of normal play, which is most of it.
     */
    this.contextBtn = this.button('tc-context', '', {
      down: () => {
        this.input.press('Space');
        this.input.setVirtualKey('Space', true);
      },
      up: () => this.input.setVirtualKey('Space', false),
    });

    this.sprintBtn = this.button('tc-sprint', 'RUN', {
      down: () => {
        this.sprintOn = !this.sprintOn;
        // Sprinting and crouching are opposite decisions about noise; a tap
        // on one is also a decision against the other.
        if (this.sprintOn) this.crouchOn = false;
        this.applyStances();
      },
    });

    this.crouchBtn = this.button('tc-crouch', 'CROUCH', {
      down: () => {
        this.crouchOn = !this.crouchOn;
        if (this.crouchOn) this.sprintOn = false;
        this.applyStances();
      },
    });

    this.interactBtn = this.button('tc-interact', '', {
      down: () => {
        this.input.press('KeyE');
        this.input.setVirtualKey('KeyE', true);
      },
      up: () => this.input.setVirtualKey('KeyE', false),
    });

    // --- Top row: one-shot utilities ------------------------------------------
    this.torchBtn = this.button('tc-mini tc-torch', 'TORCH', {
      down: () => this.input.press('KeyF'),
    });
    this.radioBtn = this.button('tc-mini tc-radio', 'RADIO', {
      down: () => this.input.press('KeyR'),
    });
    this.micBtn = this.button('tc-mini tc-mic', 'MIC', {
      down: () => this.input.press('KeyV'),
    });
    /**
     * The map is a passive that stays open on desktop, where the corner is
     * free real estate. On a phone that corner is the button row, so the map
     * becomes a toggle — routed through Input's binding mechanism with a
     * synthetic code, exactly as if it had a key.
     */
    this.mapBtn = this.button('tc-mini tc-map', 'MAP', {
      down: () => this.input.press('MapToggle'),
    });
    this.pauseBtn = this.button('tc-mini tc-pause', 'II', {
      down: () => window.dispatchEvent(new CustomEvent('static-pines:pause')),
    });

    // --- Transmit tuning: hold-to-turn, mirroring A and D ---------------------
    this.tuneLeft = this.button('tc-tune tc-tune-left', '◀', {
      down: () => this.input.setVirtualKey('KeyA', true),
      up: () => this.input.setVirtualKey('KeyA', false),
    });
    this.tuneRight = this.button('tc-tune tc-tune-right', '▶', {
      down: () => this.input.setVirtualKey('KeyD', true),
      up: () => this.input.setVirtualKey('KeyD', false),
    });

    parent.appendChild(this.root);
    this.setActive(false);
  }

  /** Show or hide the whole overlay. Hidden clears every held input. */
  setActive(active: boolean): void {
    this.root.style.display = active ? 'block' : 'none';
    if (!active) this.releaseAll();
  }

  /** Per-frame state from the game, driving lit buttons and visibility. */
  sync(state: TouchSyncState): void {
    this.torchBtn.classList.toggle('on', state.torchOn);
    this.radioBtn.classList.toggle('on', state.radioOn);

    this.micBtn.style.display = state.micStatus === 'off' ? 'none' : '';
    this.micBtn.classList.toggle('on', state.micStatus === 'live' && !state.micMuted);
    this.micBtn.classList.toggle('blocked', state.micStatus === 'denied');

    this.mapBtn.style.display = state.hasMap ? '' : 'none';
    this.mapBtn.classList.toggle('on', state.mapOpen);

    /**
     * Watching, not playing.
     *
     * A downed player keeps a stick, a run button and a torch that do
     * absolutely nothing — every one of them is gated on being alive. Clearing
     * them away leaves the two controls a spectator actually has: switch
     * subject, and drag anywhere to look.
     */
    this.setSpectating(state.spectating);

    // Space means three different things and nothing the rest of the time;
    // the button says which, and goes away when it is none of them.
    const context = state.spectating
      ? 'SWITCH'
      : state.hiding
        ? 'BREATH'
        : state.climbing
          ? 'LET GO'
          : null;

    if (context) {
      this.contextBtn.textContent = context;
      this.contextBtn.style.display = 'flex';
    } else {
      this.contextBtn.style.display = 'none';
      // A hidden button never receives its own pointerup, so anything it was
      // holding has to be released here or it stays latched — which for
      // BREATH would mean holding your breath forever after leaving a bush.
      this.input.setVirtualKey('Space', false);
    }

    if (state.prompt) {
      this.interactBtn.style.display = 'flex';
      // "E  CLIMB" reads as "CLIMB" on a button that *is* the E key.
      this.interactBtn.textContent = state.prompt.replace(/^E\s+/, '');
      this.interactBtn.classList.toggle('unavailable', state.promptDisabled);
    } else if (state.transmitting) {
      // No prompt exists during the minigame, but E still means "abandon" —
      // and a mobile player with no way to bail out of a transmission is a
      // mobile player pinned to a mast while he closes in.
      this.interactBtn.style.display = 'flex';
      this.interactBtn.textContent = 'ABANDON';
      this.interactBtn.classList.remove('unavailable');
    } else {
      this.interactBtn.style.display = 'none';
    }

    const tune = state.transmitting ? 'flex' : 'none';
    this.tuneLeft.style.display = tune;
    this.tuneRight.style.display = tune;

    this.sprintBtn.classList.toggle('on', this.sprintOn);
    this.crouchBtn.classList.toggle('on', this.crouchOn);
  }

  /**
   * Show or hide the controls that only mean something while you are alive.
   *
   * The transition also releases them: dying with a thumb on the stick would
   * otherwise leave a movement vector held down behind a hidden control, still
   * set when a teammate revives you.
   */
  private setSpectating(spectating: boolean): void {
    if (spectating !== this.spectating) {
      this.spectating = spectating;
      if (spectating) {
        this.releaseStick();
        this.sprintOn = false;
        this.crouchOn = false;
        this.applyStances();
      }
    }

    const hidden = spectating ? 'none' : '';
    this.stick.style.display = hidden;
    this.sprintBtn.style.display = hidden;
    this.crouchBtn.style.display = hidden;
    this.torchBtn.style.display = hidden;
    this.radioBtn.style.display = hidden;
  }

  dispose(): void {
    this.releaseAll();
    window.removeEventListener('pointermove', this.onPointerMove);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerUp);
    this.root.remove();
  }

  // -------------------------------------------------------------------------
  // Joystick
  // -------------------------------------------------------------------------

  private onStickDown = (e: PointerEvent): void => {
    e.stopPropagation();
    // Same takeover policy as the look surface: a fresh touch on the stick
    // always wins, so a stale pointer can never wedge movement.
    this.stickPointer = e.pointerId;

    const rect = this.stick.getBoundingClientRect();
    this.stickCentre = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
    this.moveStick(e.clientX, e.clientY);
  };

  private moveStick(x: number, y: number): void {
    let dx = (x - this.stickCentre.x) / STICK_RADIUS;
    let dy = (y - this.stickCentre.y) / STICK_RADIUS;

    const magnitude = Math.hypot(dx, dy);
    if (magnitude > 1) {
      dx /= magnitude;
      dy /= magnitude;
    }

    this.nub.style.transform = `translate(${dx * STICK_RADIUS}px, ${dy * STICK_RADIUS}px)`;

    if (magnitude < STICK_DEADZONE) {
      this.input.setVirtualMove(0, 0);
      return;
    }

    // Screen up is world forward; screen right is strafe right.
    this.input.setVirtualMove(-dy, dx);
  }

  private releaseStick(): void {
    this.stickPointer = -1;
    this.nub.style.transform = '';
    this.input.setVirtualMove(0, 0);

    // Letting go of the stick ends the sprint decision. Without this, the
    // toggle would silently re-arm the loudest thing you can do the next
    // time you nudge forward — a noise you did not choose to make.
    if (this.sprintOn) {
      this.sprintOn = false;
      this.applyStances();
    }
  }

  // -------------------------------------------------------------------------
  // Look
  // -------------------------------------------------------------------------

  private onLookDown = (e: PointerEvent): void => {
    /**
     * Anything that reached the root background is a look drag — and a new
     * touch always takes over, even if another pointer nominally holds the
     * look. Being stealable by a second thumb is a minor glitch; refusing new
     * touches because a stale pointer never sent its pointerup is a wedged
     * camera, which on a phone is unrecoverable.
     */
    this.lookPointer = e.pointerId;
    this.lookLast = { x: e.clientX, y: e.clientY };
  };

  private onPointerMove = (e: PointerEvent): void => {
    if (e.pointerId === this.stickPointer) {
      this.moveStick(e.clientX, e.clientY);
      return;
    }
    if (e.pointerId === this.lookPointer) {
      const dx = e.clientX - this.lookLast.x;
      const dy = e.clientY - this.lookLast.y;
      this.lookLast = { x: e.clientX, y: e.clientY };
      this.input.injectLook(-dx * LOOK_SENSITIVITY, -dy * LOOK_SENSITIVITY);
    }
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (e.pointerId === this.stickPointer) this.releaseStick();
    if (e.pointerId === this.lookPointer) this.lookPointer = -1;
  };

  // -------------------------------------------------------------------------
  // Plumbing
  // -------------------------------------------------------------------------

  private applyStances(): void {
    this.input.setVirtualKey('ShiftLeft', this.sprintOn);
    this.input.setVirtualKey('ControlLeft', this.crouchOn);
  }

  private releaseAll(): void {
    this.releaseStick();
    this.lookPointer = -1;
    this.sprintOn = false;
    this.crouchOn = false;
    this.applyStances();
    for (const code of ['Space', 'KeyE', 'KeyA', 'KeyD']) this.input.setVirtualKey(code, false);
  }

  private button(
    className: string,
    label: string,
    handlers: { down: () => void; up?: () => void },
  ): HTMLDivElement {
    const el = div(`tc-button ${className}`);
    el.textContent = label;

    el.addEventListener('pointerdown', (e) => {
      // Buttons are terminals, not look surfaces.
      e.stopPropagation();
      el.classList.add('pressed');
      handlers.down();
    });
    const release = (): void => {
      el.classList.remove('pressed');
      handlers.up?.();
    };
    el.addEventListener('pointerup', release);
    el.addEventListener('pointercancel', release);
    // A thumb sliding off the button must release it, not leave it held.
    el.addEventListener('pointerleave', release);

    this.root.appendChild(el);
    return el;
  }
}

function div(className: string): HTMLDivElement {
  const el = document.createElement('div');
  el.className = className;
  return el;
}
